import React, { useState } from "react";
import { Box } from "@mui/material";
import { StriimTypography, StriimFeedbackBlock } from "@striim/striim-ui";
import { AI_AGENT_NAME } from "../../ai-constants";
import styles from "./ai-config-status-banner.styles";
import StriimAIServersModal from "../striim-ai-servers-modal/striim-ai-servers-modal";

export interface AIConfigStatusBannerProps {
    isConfigured?: boolean;
    isHealthy?: boolean;
    isMisconfigured?: boolean;
    isError?: boolean;
    healthStatus?: any;
    isDemoServerHealthy?: boolean;
    onTryStriimAIServers?: () => void;
}

const isKnowledgebaseLoading = (loadedKb?: Record<string, boolean>): boolean => {
    if (!loadedKb || Object.keys(loadedKb).length === 0) return false;
    return Object.values(loadedKb).some(loaded => !loaded);
};

const AIConfigStatusBanner: React.FC<AIConfigStatusBannerProps> = ({
    isConfigured = false,
    isHealthy = true,
    isMisconfigured = false,
    isError = false,
    healthStatus,
    isDemoServerHealthy = true,
    onTryStriimAIServers
}) => {
    const [showModal, setShowModal] = useState(false);
    const showTryDemoServers = (!isConfigured || !isHealthy) && isDemoServerHealthy;

    const handleTryClick = () => {
        setShowModal(true);
    };

    const handleConfirm = () => {
        setShowModal(false);
        onTryStriimAIServers?.();
    };

    const tryDemoServersContent = (
        <StriimTypography variant="body4" color="greyscale.800">
            Just trying out {AI_AGENT_NAME}?{" "}
            <Box
                component="span"
                onClick={handleTryClick}
                sx={styles.tryServersLink}
                data-testid="try-striim-ai-servers"
            >
                Click here
            </Box>{" "}
            to use {AI_AGENT_NAME}&apos;s AI servers to get started.
        </StriimTypography>
    );

    const statusVariant = !isConfigured ? "info" : isError ? "critical" : isHealthy ? "success" : "warning";
    const statusContent = isConfigured && (
        <StriimTypography variant="body4" color="greyscale.800">
            {isHealthy ? (
                <>
                    {AI_AGENT_NAME} is currently configured and running.
                    <br />
                    You can update the configuration anytime.
                    {isKnowledgebaseLoading(healthStatus?.agent?.loaded_kb) && (
                        <>
                            <br />
                            <br />⏳ Knowledgebase is loading. It usually takes up to 10 minutes for this.
                        </>
                    )}
                </>
            ) : isError ? (
                <>
                    Unable to reach the {AI_AGENT_NAME} server.
                    <br />
                    Check your server configuration and network connectivity.
                </>
            ) : isMisconfigured ? (
                <>
                    {AI_AGENT_NAME} appears misconfigured.
                    <br />
                    Check your AI provider credentials and update the configuration.
                </>
            ) : (
                <>
                    {AI_AGENT_NAME} is configured but not reachable.
                    <br />
                    Update the configuration to restore the connection.
                </>
            )}
        </StriimTypography>
    );

    if (!statusContent && !showTryDemoServers) return null;

    return (
        <>
            <StriimAIServersModal
                isVisible={showModal}
                onConfirm={handleConfirm}
                onCancel={() => setShowModal(false)}
            />
            <Box
                sx={{
                    ...styles.wrapper,
                    "& .MuiCard-root": {
                        backgroundColor: isConfigured ? (isHealthy ? "success.50" : "warning.50") : "greyscale.50",
                        border: "1px solid",
                        borderColor: isConfigured ? (isHealthy ? "success.100" : "warning.100") : "greyscale.200",
                        "& svg": {
                            fill: isConfigured && isHealthy ? "#196738" : "none"
                        }
                    }
                }}
            >
                <StriimFeedbackBlock
                    variant={statusVariant}
                    content={statusContent || tryDemoServersContent}
                    footer={
                        statusContent && showTryDemoServers ? (
                            <Box sx={styles.tryServersFooter}>{tryDemoServersContent}</Box>
                        ) : (
                            undefined
                        )
                    }
                />
            </Box>
        </>
    );
};

export default AIConfigStatusBanner;
